import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import DatePicker from 'react-datepicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'

import { useConfig } from '@peracto/peracto-config'
import { TextInput, Select, File, Checkbox } from '@peracto/peracto-editor-ui'
import { TagsList } from '@peracto/peracto-ui'

import styled from 'styled-components'
import * as S from './styled'
import classnames from 'classnames'
import ContentProductRelationships from './ContentProductRelationships'
import BackgroundImageOptions from './BackgroundImageOptions'
import AccountNumberRestrictions from './AccountNumberRestrictions'
import UserGroupRestrictions from './UserGroupRestrictions'

const TextArea = ({ label, value, onChange }) => (
    <div className="form-group" data-testid={`${label.toLowerCase().replaceAll(' ', '-')}-section`}>
        <label className="!mb-1">{label}</label>
        <textarea
            className="form-control"
            rows={6}
            data-testid={label.toLowerCase().replaceAll(' ', '-')}
            value={value}
            onChange={onChange}
        />
    </div>
)

const LeftPanel = ({ showSettings, settings, setSettings, contentType = 'page' }) => {
    const config = useConfig()
    const { locales, restrictedCountries, allowedSlugPrefixes = [] } = config.get('editorConfig')
    const { article, content } = config.get('features', {})
    const [settingsDate, setSettingsDate] = useState()
    const [selectedFilter, setSelectedFilter] = useState(settings.type)

    useEffect(() => {
        setSelectedFilter(settings.type)
    }, [settings.type])

    const FilterToggle = styled.div`
        button {
            appearance: none;
            border: 1px solid #6c757d;
            color: #6c757d;
            height: 38px;
            width: 82px;
            background-color: transparent;

            &:active,
            &:focus {
                outline: none;
            }

            &.selected {
                background-color: #6c757d;
                color: #ddd;
            }

            &:first-child {
                border-radius: 3px 0 0 3px;
            }

            &:last-child {
                border-radius: 0 3px 3px 0;
            }
        }
    `

    const StyledDateInput = styled.div`
        display: flex;

        .react-datepicker-wrapper {
            display: block;
            width: 100%;
            flex-grow: 1;
        }
    `

    useEffect(() => {
        if (!!settings.publishedAt === false) {
            setSettingsDate(null)
        } else {
            setSettingsDate(new Date(settings.publishedAt))
        }
    }, [settings.publishedAt])

    let localeOptions

    if (locales) {
        localeOptions = locales.map(locale => ({ label: locale, value: locale }))
    } else {
        localeOptions = [{ label: 'en_GB', value: 'en_GB' }]
    }

    let showProductRelationship =
        content?.productRelationships?.hasOwnProperty(contentType) &&
        content.productRelationships[contentType] === true

    const showBabbageAccountRestrictions = content?.restrictByAccountNumber
    const showUserGroupRestrictions = content?.restrictByUserGroup

    useEffect(() => {
        document
            .getElementsByClassName('datepicker__start-date form-control')[0]
            .setAttribute('data-testid', 'published-at-field')
    }, [StyledDateInput])

    const monthOption = { month: 'long' }
    const renderDayContents = (day, date) => {
        return (
            <span
                data-testid={`date-${Intl.DateTimeFormat(localeOptions.value, monthOption)
                    .format(date)
                    .toLowerCase()}-${day}`}
            >
                {date.getDate()}
            </span>
        )
    }

    const renderCustomHeader = ({ date, customHeaderCount, decreaseMonth, increaseMonth }) => {
        return (
            <div>
                <button
                    aria-label="Previous Month"
                    className={
                        'react-datepicker__navigation react-datepicker__navigation--previous'
                    }
                    data-testid="previous-month"
                    style={customHeaderCount === 1 ? { visibility: 'hidden' } : null}
                    onClick={decreaseMonth}
                >
                    <span
                        className={
                            'react-datepicker__navigation-icon react-datepicker__navigation-icon--previous'
                        }
                    >
                        {'<'}
                    </span>
                </button>

                <span className="react-datepicker__current-month" data-testid="current-month">
                    {Intl.DateTimeFormat(localeOptions.value, monthOption).format(date)}
                </span>

                <button
                    aria-label="Next Month"
                    className={'react-datepicker__navigation react-datepicker__navigation--next'}
                    style={customHeaderCount === 0 ? { visibility: 'hidden' } : null}
                    onClick={increaseMonth}
                    data-testid="next-month"
                >
                    <span
                        className={
                            'react-datepicker__navigation-icon react-datepicker__navigation-icon--next'
                        }
                    >
                        {'>'}
                    </span>
                </button>
            </div>
        )
    }

    return (
        <S.LeftPanel
            className={classnames({
                active: showSettings,
            })}
            data-testid="left-panel"
        >
            <TextInput
                label={contentType === 'article' || contentType === 'blog' ? 'Heading' : 'Name'}
                data-testid={contentType === 'article' ? 'content-heading' : 'content-name'}
                value={settings.name}
                onChange={e => {
                    const value = e.target.value
                    setSettings({
                        ...settings,
                        name: value,
                        heading: value.trim().length > 0 ? value : settings.heading,
                    })
                }}
                onBlur={e => {
                    const trimmedValue = e.target.value.trim()
                    if (!settings.slug && trimmedValue.length > 0) {
                        const matchedPrefix = allowedSlugPrefixes.find(prefix =>
                            new RegExp(`^${prefix}/`, 'i').test(trimmedValue)
                        )

                        const slug = matchedPrefix
                            ? encodeURI(
                                  `${matchedPrefix.toLowerCase()}/${trimmedValue
                                      .slice(matchedPrefix.length + 1)
                                      .split(' ')
                                      .join('-')
                                      .replace(/[`~!@#$£€%^&*_|+=?;:'",.()<>\{\}\[\]\\]/gi, '')
                                      .replace(/-+/g, '-')
                                      .toLowerCase()}`
                              )
                            : encodeURI(
                                  trimmedValue
                                      .split(' ')
                                      .join('-')
                                      .replace(/[`~!@#$£€%^&*_|+=?;:'",.()<>\{\}\[\]\\\/]/gi, '')
                                      .replace(/-+/g, '-')
                                      .toLowerCase()
                              )

                        setSettings({
                            ...settings,
                            slug,
                        })
                    }
                }}
            />
            {(contentType === 'article' || 'blog') && (
                <TextArea
                    label="Short Description"
                    value={settings.shortDescription || ''}
                    onChange={e =>
                        setSettings({
                            ...settings,
                            shortDescription: e.target.value,
                        })
                    }
                />
            )}
            {!['template', 'element'].includes(contentType) && (
                <>
                    <TextInput
                        label="Title"
                        data-testid="content-title"
                        value={settings.title.length > 0 ? settings.title : ''}
                        onChange={e => {
                            setSettings({
                                ...settings,
                                title: e.target.value,
                            })
                        }}
                    />
                    <TextArea
                        label="Meta Description"
                        data-testid="content-meta-desc"
                        value={settings.metaDescription || ''}
                        onChange={e =>
                            setSettings({
                                ...settings,
                                metaDescription: e.target.value,
                            })
                        }
                    />
                </>
            )}
            {['page', 'article', 'blog', 'template'].includes(contentType) && (
                <TagsList
                    label="Keywords"
                    testId="keywords-field"
                    value={settings.keywords}
                    onChange={tags => {
                        setSettings({
                            ...settings,
                            keywords: tags,
                        })
                    }}
                />
            )}
            {['page', 'article', 'blog'].includes(contentType) && content?.indexContent && (
                <div className="form-group">
                    <Checkbox
                        label="Exclude From Search"
                        checked={!settings.searchable}
                        onChange={e => setSettings({ ...settings, searchable: !e.target.checked })}
                    />
                </div>
            )}
            {!['template', 'element'].includes(contentType) && (
                <>
                    <File
                        label="Image"
                        value={settings.image}
                        showThumbnail={true}
                        onChange={value =>
                            setSettings({
                                ...settings,
                                image: value,
                            })
                        }
                        data-testid="content-image"
                    />

                    <TextInput
                        label="Slug"
                        data-testid="content-slug"
                        value={settings.slug}
                        onChange={e =>
                            setSettings({
                                ...settings,
                                slug: e.target.value,
                            })
                        }
                        onBlur={e => {
                            if (e.target.value.length > 0) {
                                const rawValue = e.target.value.trim()
                                const matchedPrefix = allowedSlugPrefixes.find(prefix =>
                                    new RegExp(`^${prefix}/`, 'i').test(rawValue)
                                )

                                const slug = matchedPrefix
                                    ? encodeURI(
                                          `${matchedPrefix.toLowerCase()}/${rawValue
                                              .slice(matchedPrefix.length + 1)
                                              .split(' ')
                                              .join('-')
                                              .replace(
                                                  /[`~!@#$£€%^&*_|+=?;:'",.()<>\{\}\[\]\\]/gi,
                                                  ''
                                              )
                                              .replace(/-+/g, '-')
                                              .toLowerCase()}`
                                      )
                                    : encodeURI(
                                          rawValue
                                              .split(' ')
                                              .join('-')
                                              .replace(
                                                  /[`~!@#$£€%^&*_|+=?;:'",.()<>\{\}\[\]\\\/]/gi,
                                                  ''
                                              )
                                              .replace(/-+/g, '-')
                                              .toLowerCase()
                                      )

                                setSettings({
                                    ...settings,
                                    slug,
                                })
                            }
                        }}
                        required
                    />
                </>
            )}
            <div className="form-group" data-testid="published-at-section">
                <label className="!mb-1">Published At</label>
                <StyledDateInput data-testid="published-at">
                    <DatePicker
                        renderCustomHeader={renderCustomHeader}
                        renderDayContents={renderDayContents}
                        className="datepicker__start-date form-control"
                        selected={settingsDate}
                        onChange={date => {
                            setSettingsDate(date > 0 ? date : null)
                            setSettings({
                                ...settings,
                                publishedAt: date > 0 ? dayjs(new Date(date)).format() : null,
                            })
                        }}
                        dateFormat={`dd/MM/yyyy HH:mm`}
                        showTimeSelect={true}
                        timeFormat="HH:mm"
                        timeCaption="Time"
                    />

                    <button
                        className="!ml-2 btn btn-outline-secondary !whitespace-nowrap"
                        onClick={() => {
                            setSettings({
                                ...settings,
                                publishedAt: null,
                            })
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} className="!mr-1" />
                        Clear
                    </button>
                </StyledDateInput>
            </div>
            {localeOptions?.length > 1 && (
                <Select
                    label="Locale"
                    testId="locale-dropdown"
                    value={settings.locale}
                    onChange={e =>
                        setSettings({
                            ...settings,
                            locale: e.value,
                        })
                    }
                    options={localeOptions}
                />
            )}

            {article?.blogToggle && (
                <>
                    {['article', 'blog'].includes(contentType) && content?.indexContent && (
                        <div className="form-group" data-testid="article-toggle">
                            <label className="!mb-1">This content is an: &nbsp;</label>
                            <FilterToggle>
                                <button
                                    onClick={() => {
                                        setSelectedFilter('article')
                                        setSettings({
                                            ...settings,
                                            type: 'article',
                                        })
                                    }}
                                    className={selectedFilter === 'article' ? 'selected' : ''}
                                    data-testid="filter-article"
                                >
                                    Article
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedFilter('blog')
                                        setSettings({
                                            ...settings,
                                            type: 'blog',
                                        })
                                    }}
                                    className={selectedFilter === 'blog' ? 'selected' : ''}
                                    data-testid="filter-blog"
                                >
                                    Blog
                                </button>
                            </FilterToggle>
                        </div>
                    )}
                </>
            )}

            {!['template', 'element'].includes(contentType) && (
                <>
                    <hr />

                    <BackgroundImageOptions
                        settings={settings}
                        setSettings={setSettings}
                        canContainBackground={contentType === 'article' || 'blog'}
                    />
                </>
            )}

            {showProductRelationship && (
                <>
                    <hr className="!mt-6" />

                    <ContentProductRelationships settings={settings} setSettings={setSettings} />
                </>
            )}

            {restrictedCountries?.length > 0 && (
                <>
                    <hr className="!mt-6" />

                    <Select
                        label="Restrict to Country"
                        testId="country-restriction-dropdown"
                        value={settings.countryCode}
                        onChange={e =>
                            setSettings({
                                ...settings,
                                countryCode: e.value,
                            })
                        }
                        options={[{ label: 'All', value: 'all' }, ...restrictedCountries]}
                    />
                </>
            )}

            {showBabbageAccountRestrictions && (
                <>
                    <hr className="!mt-6" />

                    <AccountNumberRestrictions settings={settings} setSettings={setSettings} />
                </>
            )}

            {showUserGroupRestrictions && (
                <>
                    <hr className="!mt-6" />

                    <UserGroupRestrictions settings={settings} setSettings={setSettings} />
                </>
            )}
        </S.LeftPanel>
    )
}

export default LeftPanel
